import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FeatureForbiddenReason,
  PlatformNavigationContext
} from '@/app/platform/platform-context/platform-navigation.context';
import { FeatureElementAccessToggler } from '@/app/platform/platform-context/feature-element-access-toggler';

@Directive({
    selector: '[featureAccess]',
    standalone: false
})
/**
 * Disables the element when the feature is not allowed for the active user on the current NavigationItem.
 */
export class FeatureAccessDirective implements OnInit, OnDestroy {
  @Input('featureAccess') name: string;

  @Input() forbiddenIndicationStrategy:
    /**
     * Disable the element
     */
    'disable'
    /**
     * Hide the element
     */
    | 'hide'
    /**
     * Protect the element with an overlay and display the reasons why the element is not accessible
     */
    | 'overlayWithReason' = 'disable';

  constructor(
    private readonly egiPageContext: PlatformNavigationContext,
    private readonly el: ElementRef<HTMLElement>,
    private readonly featureElementAccessToggler: FeatureElementAccessToggler
  ) {}

  private featureAllowedSubscription?: Subscription;

  ngOnInit(): void {
    if (!this.el.nativeElement.style.position) {
      this.el.nativeElement.style.position = 'relative';
    }
    this.featureAllowedSubscription = this.egiPageContext
      .featureForbiddenReasons$(this.name)
      .subscribe(reasons => {
        const isAllowed = reasons.length === 0;
        if (isAllowed) {
          this.enableActionsForComponent();
        } else {
          this.disableActionsForComponent(reasons);
        }
      });
  }

  ngOnDestroy() {
    this.featureAllowedSubscription?.unsubscribe();
  }

  private disableActionsForComponent(reasons: FeatureForbiddenReason[]): void {
    if (this.forbiddenIndicationStrategy === 'hide') {
      this.el.nativeElement.hidden = true;
      this.el.nativeElement.classList.add('hidden');
    } else if (this.forbiddenIndicationStrategy === 'disable') {
      this.featureElementAccessToggler.disable(this.el);
    } else if (this.forbiddenIndicationStrategy === 'overlayWithReason') {
      this.featureElementAccessToggler.showUnauthorizedOverlay(this.el, this.name, reasons);
    }
  }

  private enableActionsForComponent(): void {
    if (this.forbiddenIndicationStrategy === 'hide') {
      this.el.nativeElement.hidden = false;
      this.el.nativeElement.classList.remove('hidden');
    } else if (this.forbiddenIndicationStrategy === 'disable') {
      this.featureElementAccessToggler.enable(this.el);
    } else if (this.forbiddenIndicationStrategy === 'overlayWithReason') {
      this.featureElementAccessToggler.hideUnauthorizedOverlay(this.el);
    }
  }
}
